import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { SettingsCard } from '@allrouted/ui/Field';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Whether a driver can sign in to the driver app, and the password they use.
 *
 * They sign in with their email -- which can belong to only one driver across
 * AllRouted -- and a password set here. A generated one is shown once, to
 * pass on; nobody can read it back afterwards.
 */

export interface DriverSignInState {
  id: string;
  name: string;
  email: string | null;
  active: boolean;
  has_password?: boolean;
  password_set_at?: string | null;
  password_set_by?: string | null;
  app_last_login_at?: string | null;
  app_locked?: boolean;
  /* Devices currently signed in to the app. */
  app_sessions?: number;
}

const MIN = 8;
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : 'never');

export function DriverSignIn({
  territoryId,
  driver,
  onChanged,
}: {
  territoryId: string;
  driver: DriverSignInState;
  onChanged: () => void;
}) {
  const ui = useUi();
  const [setting, setSetting] = useState(false);
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const hasEmail = !!driver.email?.trim();

  const save = async (generate: boolean) => {
    if (!generate && password.length < MIN) {
      ui.notify('error', 'Too short', `A driver password needs at least ${MIN} characters.`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.setDriverPassword(territoryId, driver.id, generate ? undefined : password);
      if (r.password) {
        setIssued(r.password);
        setCopied(false);
      } else {
        ui.notify('ok', 'Password set', `${driver.name} signs in to the app as ${driver.email}.`);
      }
      setSetting(false);
      setPassword('');
      onChanged();
    } catch (err) {
      ui.notify('error', 'Could not set the password', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const signOutEverywhere = async () => {
    const ok = await ui.confirm({
      title: `Sign ${driver.name} out of the app?`,
      body: 'Every phone or tablet they are signed in on is signed out. Their password stays, so they can sign straight back in.',
      confirmLabel: 'Sign out everywhere',
    });
    if (!ok) return;
    try {
      await api.signOutDriverApp(territoryId, driver.id);
      ui.notify('ok', 'Signed out of the app', `${driver.name} needs to sign in again on every device.`);
      onChanged();
    } catch (err) {
      ui.notify('error', 'Could not sign them out', err instanceof ApiError ? err.message : String(err));
    }
  };

  const turnOff = async () => {
    const ok = await ui.confirm({
      title: `Turn off app sign-in for ${driver.name}?`,
      body: 'Their password is removed, every device they are signed in on is signed out, and they can no longer sign in to the driver app. You can set a new one at any time.',
      confirmLabel: 'Turn off',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.clearDriverPassword(territoryId, driver.id);
      ui.notify('ok', 'App sign-in turned off');
      onChanged();
    } catch (err) {
      ui.notify('error', 'Could not turn it off', err instanceof ApiError ? err.message : String(err));
    }
  };

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued);
      setCopied(true);
    } catch {
      ui.notify('info', 'Select and copy it', 'The browser would not give us the clipboard.');
    }
  };

  return (
    <SettingsCard
      title="Driver app sign-in"
      description="They sign in to the driver app with their email and this password."
      editing={false}
    >
      {issued ? (
        <div className="key-reveal">
          <strong>Give this password to {driver.name}. It is not shown again.</strong>
          <p>
            They sign in as <b>{driver.email}</b>. Nobody can read it back later; set a new one if it is lost.
          </p>
          <div className="key-value">
            <code>{issued}</code>
            <button type="button" className="btn" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button type="button" className="btn" onClick={() => setIssued(null)}>
            Done
          </button>
        </div>
      ) : null}

      <div className="driver-signin">
        <div className="driver-signin-state">
          {!hasEmail ? (
            <span className="pill" data-tone="idle">
              No email
            </span>
          ) : driver.has_password ? (
            <span className="pill" data-tone={driver.active ? 'ok' : 'warn'}>
              {driver.active ? 'Can sign in' : 'Password set, but driver inactive'}
            </span>
          ) : (
            <span className="pill" data-tone="idle">
              No password
            </span>
          )}
          {driver.app_locked ? (
            <span className="pill" data-tone="late">
              Locked for 15 min after wrong passwords
            </span>
          ) : null}
        </div>

        {hasEmail ? (
          <dl className="driver-signin-facts">
            <div>
              <dt>Signs in as</dt>
              <dd>{driver.email}</dd>
            </div>
            {driver.has_password ? (
              <>
                <div>
                  <dt>Password set</dt>
                  <dd>
                    {when(driver.password_set_at)}
                    {driver.password_set_by ? ` by ${driver.password_set_by}` : ''}
                  </dd>
                </div>
                <div>
                  <dt>Last app sign-in</dt>
                  <dd>{when(driver.app_last_login_at)}</dd>
                </div>
                <div>
                  <dt>Signed in on</dt>
                  <dd>
                    {driver.app_sessions ?? 0} device{(driver.app_sessions ?? 0) === 1 ? '' : 's'}
                  </dd>
                </div>
              </>
            ) : null}
          </dl>
        ) : (
          <p className="hint">Add an email address to this driver (Edit, above) to let them sign in to the app.</p>
        )}

        {hasEmail && setting ? (
          <div className="driver-signin-form">
            <input
              className="field mono"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              placeholder={`New password, ${MIN}+ characters`}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save(false);
              }}
              autoFocus
            />
            <button type="button" className="btn btn-small" onClick={() => setShow((v) => !v)}>
              {show ? 'Hide' : 'Show'}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy || password.length < MIN} onClick={() => void save(false)}>
              Save password
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void save(true)}>
              Generate one instead
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setSetting(false);
                setPassword('');
              }}
            >
              Cancel
            </button>
          </div>
        ) : null}

        {hasEmail && !setting ? (
          <div className="driver-signin-actions">
            <button type="button" className="btn btn-primary" onClick={() => setSetting(true)}>
              {driver.has_password ? 'Change password' : 'Set password'}
            </button>
            {(driver.app_sessions ?? 0) > 0 ? (
              <button type="button" className="btn" onClick={() => void signOutEverywhere()}>
                Sign out of the app everywhere
              </button>
            ) : null}
            {driver.has_password ? (
              <button type="button" className="btn btn-danger" onClick={() => void turnOff()}>
                Turn off sign-in
              </button>
            ) : null}
          </div>
        ) : null}

        {!driver.active && driver.has_password ? (
          <p className="hint">Inactive drivers are refused at sign-in. Turn them back on with Edit, above.</p>
        ) : null}
      </div>
    </SettingsCard>
  );
}
